/**
 * useAiTrailer
 *
 * Drives an AI trailer-generation job and exposes the same small state machine
 * shape as useAiVideoAnalysis, so the UI can render a familiar progress panel.
 * Owns no form knowledge — it runs the service, tracks stage/progress, and hands
 * back the finished trailer descriptor. The Trailer section decides what to do
 * with it (prefill the trailer card, show a preview, etc).
 *
 * Status lifecycle:
 *   idle → running → complete
 *                 ↳ failed  (retry via generate())
 *                 ↳ idle    (reset())
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { generateTrailer, TRAILER_STAGES } from "../services/ai";

export const TRAILER_STATUS = {
  IDLE: "idle",
  RUNNING: "running",
  COMPLETE: "complete",
  FAILED: "failed",
};

export default function useAiTrailer() {
  const [status, setStatus] = useState(TRAILER_STATUS.IDLE);
  const [activeStage, setActiveStage] = useState(null);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const abortRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStatus(TRAILER_STATUS.IDLE);
    setActiveStage(null);
    setProgress(0);
    setResult(null);
    setError(null);
  }, []);

  /**
   * Start (or restart) trailer generation for a video.
   * @param {object} video
   * @param {object} [opts] { durationTarget, title }
   */
  const generate = useCallback((video, opts = {}) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus(TRAILER_STATUS.RUNNING);
    setError(null);
    setResult(null);
    setProgress(0);
    setActiveStage(TRAILER_STAGES[0]?.id ?? null);

    generateTrailer({
      video,
      durationTarget: opts.durationTarget,
      title: opts.title,
      signal: controller.signal,
      onStage: (stageId, pct) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        if (stageId !== "complete") setActiveStage(stageId);
        if (typeof pct === "number") setProgress(pct);
      },
    })
      .then((res) => {
        if (!mountedRef.current || controller.signal.aborted) return;
        setResult(res);
        setProgress(100);
        setActiveStage("complete");
        setStatus(TRAILER_STATUS.COMPLETE);
      })
      .catch((err) => {
        if (err?.name === "AbortError") return;
        if (!mountedRef.current) return;
        setError(err?.message || "Trailer generation failed. You can retry or add one manually.");
        setStatus(TRAILER_STATUS.FAILED);
      });

    return controller;
  }, []);

  return {
    status,
    stages: TRAILER_STAGES,
    activeStage,
    progress,
    result,
    error,
    generate,
    reset,
    isIdle: status === TRAILER_STATUS.IDLE,
    isRunning: status === TRAILER_STATUS.RUNNING,
    isComplete: status === TRAILER_STATUS.COMPLETE,
    isFailed: status === TRAILER_STATUS.FAILED,
  };
}
